import { takeLatest, put, call, select, delay, take } from 'redux-saga/effects';
import { IToggle, UnleashClient } from 'unleash-proxy-client';
import { featureFlagActions } from '../redux/feature-flag/slice';
import { eventChannel } from 'redux-saga';
import { RootState } from '../redux';
import { UserState } from '../redux/user/slice';
import { UserSelectors } from '../redux/selectors';

type CustomUnleashClient = UnleashClient & {
  REFRESH_INTERVAL_ON_APP_RESUME?: number;
  // lastRefreshTimestamp -- this is private method in UnleashClient
};

let unleashClient: CustomUnleashClient | null = null;

export function setUnleashContext(fieldName: string, value: string) {
  if (unleashClient) {
    unleashClient.setContextField(fieldName, value);
  }
}

export function removeUnleashContext(fieldName: string) {
  if (unleashClient) {
    unleashClient.removeContextField(fieldName);
  }
}

export function refreshUnleash() {
  if (unleashClient) {
    if (unleashClient) {
      const lastRefresh = unleashClient['lastRefreshTimestamp'];
      const now = Date.now();
      // If the last refresh was less than x seconds, skip the update
      if (now - lastRefresh < (unleashClient.REFRESH_INTERVAL_ON_APP_RESUME || 0) * 1000) {
        return;
      }
    }
    unleashClient.updateToggles();
  }
}

function createUnleashChannel(client: UnleashClient) {
  return eventChannel((emit) => {
    const handler = () => {
      const toggles = client?.getAllToggles() || [];
      const togglesObj = toggles.reduce((acc, toggle) => {
        acc[toggle.name] = toggle;
        return acc;
      }, {} as Record<string, IToggle>);
      emit(togglesObj);
    };

    client.on('update', handler);
    return () => {
      // Cleanup if saga is canceled
      client.off('update', handler);
    };
  });
}

function* handleUnleashUpdates(client: UnleashClient) {
  const channel = yield call(createUnleashChannel, client);
  try {
    while (true) {
      const updatedFeatures: Record<string, IToggle> = yield take(channel);
      yield put(featureFlagActions.setFeatures(updatedFeatures));
    }
  } finally {
    channel.close();
  }
}

function* handleStartUnleashClient(
  action: ReturnType<typeof featureFlagActions.startUnleashClient>,
) {
  if (unleashClient) {
    yield call([unleashClient, unleashClient.stop]);
  }

  const {
    APP_NAME,
    CLIENT_KEY,
    ENVIRONMENT,
    URL,
    REFRESH_INTERVAL,
    REFRESH_INTERVAL_ON_APP_RESUME,
    platform,
  } = action.payload;

  unleashClient = new UnleashClient({
    url: URL,
    clientKey: CLIENT_KEY,
    appName: APP_NAME,
    environment: ENVIRONMENT,
    refreshInterval: REFRESH_INTERVAL || 0,
    ...(platform !== 'web' && {
      storageProvider: {
        get: async (k) => {}, // no-op function
        save: async (k, v) => {}, // no-op function
      },
      experimental: {
        togglesStorageTTL: 99999999999, // so that it updates lastRefreshTimestamp
      },
    }),
  });

  unleashClient.REFRESH_INTERVAL_ON_APP_RESUME = REFRESH_INTERVAL_ON_APP_RESUME;

  // set context for the client
  const user: UserState = yield select(UserSelectors.user);
  if (user && user.account && user.account.id) {
    yield call(setUnleashContext, 'userId', String(user.account.id));
  }
  if (platform) {
    yield call(setUnleashContext, 'platform', platform as string);
  }

  unleashClient.start();
  yield call(handleUnleashUpdates, unleashClient);
}

export default function* unleashSaga() {
  yield takeLatest(featureFlagActions.startUnleashClient.type, handleStartUnleashClient);
}
